import { 
    Accordion, 
    AccordionDetails, 
    AccordionSummary, 
    Box, 
    Grid, 
    IconButton, 
    Tooltip, 
    Typography, 
    useMediaQuery,
    Container,
    Card,
    Fade,
    Zoom,
    Paper
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Thumbs, EffectFade, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/thumbs";
import "swiper/css/effect-fade";
import { ArrowBackIos, ArrowForwardIos, PhotoLibrary } from "@mui/icons-material";

export const GallerySection = ({ endpointsUrl, instituteRes }) => {

    const isMobile = useMediaQuery("(max-width:900px)");
    const isTablet = useMediaQuery("(max-width:1200px)");
    const [thumbsSwiper, setThumbsSwiper] = useState(null);
    const [selectedImage, setSelectedImage] = useState("");
    const [isVisible, setIsVisible] = useState(false);
    const [hoveredThumb, setHoveredThumb] = useState(null);
    const prevRef = useRef(null);
    const nextRef = useRef(null);

    useEffect(() => {
        if (instituteRes?.gallery) {
            setSelectedImage(instituteRes?.gallery?.[0] || '')
        }
        // Trigger animation after component mounts
        const timer = setTimeout(() => setIsVisible(true), 100);
        return () => clearTimeout(timer);
    }, [instituteRes])

    return (
        <Box
            sx={{
                position: 'relative',
                py: { xs: 4, md: 6 },
                px: { xs: 1, md: 2 },
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at 20% 80%, rgba(59, 130, 246, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(168, 85, 247, 0.1) 0%, transparent 50%)',
                    pointerEvents: 'none',
                }
            }}
        >
            <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
                {instituteRes?.gallery?.length > 0 && (
                    <>
                        {/* Header Section */}
                        <Fade in={isVisible} timeout={800}>
                            <Box sx={{ textAlign: 'center', mb: 5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                    <PhotoLibrary sx={{ 
                                        fontSize: { xs: '2rem', md: '2.5rem' }, 
                                        color: '#3b82f6', 
                                        mr: 2 
                                    }} />
                                    <Typography 
                                        variant="h3"
                                        sx={{ 
                                            fontWeight: 700,
                                            color: 'white',
                                            fontSize: { xs: '1.6rem', md: '2.4rem' },
                                            position: 'relative',
                                        }}
                                    >
                                        Our Gallery
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.9)',
                                        fontSize: { xs: '0.9rem', md: '1.1rem' },
                                        maxWidth: 600,
                                        mx: 'auto',
                                        lineHeight: 1.6,
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        fontWeight: 600
                                    }}
                                >
                                    VSmart ExamPrep - Known for our Qualitative Learning Excellence
                                </Typography>
                                
                                {/* Decorative Line */}
                                <Box
                                    sx={{
                                        width: 80,
                                        height: 4,
                                        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                                        borderRadius: 2,
                                        mx: 'auto',
                                        mt: 3,
                                    }}
                                />
                            </Box>
                        </Fade>

                        {/* Main Gallery Container */}
                        <Zoom in={isVisible} timeout={1000}>
                            <Card
                                sx={{
                                    background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                                    backdropFilter: 'blur(20px)',
                                    borderRadius: '24px',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    p: { xs: 2, md: 4 },
                                    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.2)',
                                }}
                            >
                                {/* Main Image Display */}
                                <Box
                                    sx={{
                                        position: 'relative',
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        mb: 4,
                                        borderRadius: "20px",
                                        overflow: "hidden",
                                        background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                                        p: 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            borderRadius: '16px',
                                            overflow: 'hidden',
                                            width: '100%',
                                            maxWidth: { xs: '100%', md: '55%' },
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                background: 'linear-gradient(45deg, transparent 0%, rgba(59, 130, 246, 0.1) 50%, transparent 100%)',
                                                zIndex: 1,
                                                pointerEvents: 'none',
                                            }
                                        }}
                                    >
                                        <img
                                            src={endpointsUrl + selectedImage}
                                            alt="Selected Gallery Image"
                                            style={{
                                                width: "100%",
                                                height: isMobile ? "300px" : "600px",
                                                // objectFit: "cover",
                                                borderRadius: "16px",
                                                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
                                                transition: "all 0.5s ease",
                                            }}
                                        />
                                    </Box>
                                </Box>

                                {/* Thumbnail Navigation */}
                                <Paper
                                    elevation={0}
                                    sx={{
                                        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                                        backdropFilter: 'blur(10px)',
                                        borderRadius: "20px",
                                        p: 2,
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 2,
                                    }}
                                >
                                    {/* Previous Button */}
                                    <IconButton 
                                        ref={prevRef} 
                                        sx={{ 
                                            bgcolor: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                            background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                            color: 'white',
                                            width: { xs: 40, md: 48 },
                                            height: { xs: 40, md: 48 },
                                            boxShadow: "0 8px 20px rgba(59, 130, 246, 0.3)",
                                            '&:hover': {
                                                background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                                                transform: 'translateY(-2px)',
                                                boxShadow: "0 12px 25px rgba(59, 130, 246, 0.4)",
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        <ArrowBackIos sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />
                                    </IconButton>

                                    {/* Thumbnail Swiper */}
                                    <Box sx={{ 
                                        flex: 1, 
                                        maxWidth: { xs: '70%', md: '80%' },
                                        '& .swiper': {
                                            paddingY: 1,
                                        }
                                    }}>
                                        <Swiper
                                            onSwiper={setThumbsSwiper}
                                            spaceBetween={isMobile ? 8 : 12}
                                            slidesPerView={isMobile ? 3 : isTablet ? 5 : 7}
                                            watchSlidesProgress
                                            modules={[Navigation, Thumbs]}
                                            navigation={{
                                                prevEl: prevRef.current,
                                                nextEl: nextRef.current
                                            }}
                                            style={{ padding: '4px 0' }}
                                        >
                                            {instituteRes?.gallery.map((item, i) => (
                                                <SwiperSlide key={`thumb-${i}`}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        <Box
                                                            onMouseEnter={() => setHoveredThumb(i)}
                                                            onMouseLeave={() => setHoveredThumb(null)}
                                                            sx={{
                                                                position: 'relative',
                                                                border: selectedImage === item 
                                                                    ? "3px solid #3b82f6" 
                                                                    : "2px solid rgba(255, 255, 255, 0.2)",
                                                                borderRadius: "50%",
                                                                p: "3px",
                                                                transition: "all 0.3s ease",
                                                                cursor: "pointer",
                                                                transform: selectedImage === item 
                                                                    ? 'scale(1.1)' 
                                                                    : hoveredThumb === i 
                                                                        ? 'scale(1.05)' 
                                                                        : 'scale(1)',
                                                                boxShadow: selectedImage === item 
                                                                    ? '0 8px 25px rgba(59, 130, 246, 0.4)'
                                                                    : hoveredThumb === i 
                                                                        ? '0 6px 20px rgba(255, 255, 255, 0.2)'
                                                                        : '0 4px 12px rgba(0, 0, 0, 0.1)',
                                                                '&::before': selectedImage === item ? {
                                                                    content: '""',
                                                                    position: 'absolute',
                                                                    top: -2,
                                                                    left: -2,
                                                                    right: -2,
                                                                    bottom: -2,
                                                                    background: 'conic-gradient(from 0deg, #3b82f6, #8b5cf6, #3b82f6)',
                                                                    borderRadius: '50%',
                                                                    zIndex: -1,
                                                                    animation: 'rotate 3s linear infinite',
                                                                    '@keyframes rotate': {
                                                                        '0%': { transform: 'rotate(0deg)' },
                                                                        '100%': { transform: 'rotate(360deg)' },
                                                                    }
                                                                } : {}
                                                            }}
                                                            onClick={() => setSelectedImage(item)}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    borderRadius: "50%",
                                                                    overflow: "hidden",
                                                                    width: { xs: "60px", md: "80px" },
                                                                    height: { xs: "60px", md: "80px" },
                                                                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
                                                                    backdropFilter: 'blur(10px)',
                                                                }}
                                                            >
                                                                <img
                                                                    src={endpointsUrl + item}
                                                                    alt={`Gallery thumbnail ${i + 1}`}
                                                                    style={{
                                                                        width: "100%",
                                                                        height: "100%",
                                                                        objectFit: "cover",
                                                                        borderRadius: "50%",
                                                                        filter: selectedImage === item 
                                                                            ? 'brightness(1.1) contrast(1.1)' 
                                                                            : 'brightness(0.9)',
                                                                        transition: 'all 0.3s ease',
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                </SwiperSlide>
                                            ))}
                                        </Swiper>
                                    </Box>

                                    {/* Next Button */}
                                    <IconButton 
                                        ref={nextRef} 
                                        sx={{ 
                                            bgcolor: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                            background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
                                            color: 'white',
                                            width: { xs: 40, md: 48 },
                                            height: { xs: 40, md: 48 },
                                            boxShadow: "0 8px 20px rgba(59, 130, 246, 0.3)",
                                            '&:hover': {
                                                background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                                                transform: 'translateY(-2px)',
                                                boxShadow: "0 12px 25px rgba(59, 130, 246, 0.4)",
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        <ArrowForwardIos sx={{ fontSize: { xs: '1rem', md: '1.2rem' } }} />
                                    </IconButton>
                                </Paper>
                            </Card>
                        </Zoom>
                    </>
                )}
            </Container>
        </Box>
    );
};
